import { useCallback } from 'react';

import { ApolloQueryResult, useApolloClient } from '@apollo/client';

import {
  ILoyaltyPromotionInput,
  ILoyaltyUserOffersQuery,
  ILoyaltyUserRewardsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyUserOffersDocument,
  LoyaltyUserRewardsDocument,
  ServiceMode,
} from 'generated/graphql-gateway';
import { parseOffer, parseReward } from 'state/global-state/models/loyalty/offers/offers.utils';
import logger from 'utils/logger';

import { LoyaltyAppliedOffer } from '../types';

import {
  IAppliedRewards,
  ICartEntryAdapter,
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from './types';
import { createFeedbackMaps } from './utils/helpers';

interface IBaseLoyaltyIncentivesEvaluationParams {
  cartEntries?: ICartEntryAdapter[];
  loyaltyId: string;
  paymentMethod?: LoyaltyPaymentMethod | null;
  serviceMode?: ServiceMode | null;
  storeId?: string | null;
  subtotalAmount?: number;
  options?: {
    incompleteEvaluation?: boolean;
    omitInvalids?: boolean;
    redemptionTypes?: LoyaltyOfferRedemptionType[];
  };
}

interface ILoyaltyOfferEvaluationParams extends IBaseLoyaltyIncentivesEvaluationParams {
  incentiveType: LoyaltyPromotionType.OFFER;
  appliedIncentives: LoyaltyAppliedOffer[];
}

interface ILoyaltyRewardEvaluationParams extends IBaseLoyaltyIncentivesEvaluationParams {
  incentiveType: LoyaltyPromotionType.REWARD;
  appliedIncentives: IAppliedRewards;
}

type EvaluateLoyaltyIncentivesParams =
  | ILoyaltyOfferEvaluationParams
  | ILoyaltyRewardEvaluationParams;

const OFFER_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

const REWARD_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.REWARD_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

export type LoyaltyEvaluationParams = {
  loyaltyId: string;
  appliedIncentives: ILoyaltyPromotionInput[];
  cartEntries?: ICartEntryAdapter[];
  paymentMethod?: LoyaltyPaymentMethod | null;
  serviceMode?: ServiceMode | null;
  storeId?: string | null;
  subtotalAmount?: number;
  options?: {
    incompleteEvaluation?: boolean;
    omitInvalids?: boolean;
    redemptionTypes?: LoyaltyOfferRedemptionType[];
  };
};

const getLoyaltyEvaluationParams = (
  params: ILoyaltyOfferEvaluationParams | ILoyaltyRewardEvaluationParams
) => {
  let appliedIncentives: ILoyaltyPromotionInput[];
  if (params.incentiveType === LoyaltyPromotionType.OFFER) {
    appliedIncentives = params.appliedIncentives
      .map(offer => parseOffer(offer))
      .filter((offer): offer is ILoyaltyPromotionInput => offer !== null);
  } else {
    appliedIncentives = Object.keys(params.appliedIncentives)
      .map(cartId => parseReward(params.appliedIncentives[cartId], cartId))
      .filter((reward): reward is ILoyaltyPromotionInput => reward !== null);
  }
  return {
    loyaltyId: params.loyaltyId,
    appliedIncentives,
    cartEntries: params.cartEntries,
    paymentMethod: params.paymentMethod,
    serviceMode: params.serviceMode,
    storeId: params.storeId,
    subtotalAmount: params.subtotalAmount,
    options: params.options,
  } as LoyaltyEvaluationParams;
};

export const useLoyaltyIncentivesEvaluation = () => {
  const client = useApolloClient();

  const evaluateLoyaltyOffers = useCallback(
    async ({ loyaltyId, options = {}, appliedIncentives, ...rest }: LoyaltyEvaluationParams) => {
      const {
        omitInvalids = false,
        incompleteEvaluation = false,
        redemptionTypes = [LoyaltyOfferRedemptionType.STANDARD],
      } = options;
      let evaluationResult: IncentiveEvaluationMap | undefined;

      try {
        const { data }: ApolloQueryResult<ILoyaltyUserOffersQuery> = await client.query({
          query: LoyaltyUserOffersDocument,
          fetchPolicy: 'network-only',
          variables: {
            loyaltyId,
            where: {
              appliedIncentives,
              ids: appliedIncentives.map(({ id }) => id),
              ...rest,
              omitInvalids,
              // This boolean forces a full evaluation of all applied offers rules
              isTransactionCreation: !incompleteEvaluation,
              redemptionTypes,
            },
          },
        });
        const offers = data?.loyaltyUserV2?.offers;
        if (offers) {
          const { incentiveFeedbackMap, incentivesMapById } = createFeedbackMaps(offers);
          // mapping no longer existing offers
          // if the offer didn't come back on the response then it doesn't exist
          appliedIncentives.forEach(({ id }) => {
            if (id && !incentivesMapById[id]) {
              incentiveFeedbackMap[id] = [OFFER_NOT_AVAILABLE_EVALUATION_RESULT];
            }
          });
          evaluationResult = incentiveFeedbackMap;
        }
      } catch (e) {
        logger.error(`Offers evaluation error: ${e}`);
        throw e;
      }

      return evaluationResult;
    },
    [client]
  );

  const evaluateLoyaltyRewards = useCallback(
    async ({ loyaltyId, options = {}, appliedIncentives, ...rest }: LoyaltyEvaluationParams) => {
      const { omitInvalids = false, incompleteEvaluation = false } = options;
      let evaluationResult: IncentiveEvaluationMap | undefined;

      try {
        const { data }: ApolloQueryResult<ILoyaltyUserRewardsQuery> = await client.query({
          query: LoyaltyUserRewardsDocument,
          fetchPolicy: 'network-only',
          variables: {
            loyaltyId,
            where: {
              appliedIncentives,
              rewardIds: appliedIncentives.map(({ id }) => id),
              ...rest,
              omitInvalids,
              // This boolean forces a full evaluation of all applied offers rules
              isTransactionCreation: !incompleteEvaluation,
            },
          },
        });
        const rewards = data?.loyaltyUserV2?.rewards;
        if (rewards) {
          const { incentiveFeedbackMap, incentivesMapById } = createFeedbackMaps(rewards);
          // mapping no longer existing offers
          // if the offer didn't come back on the response then it doesn't exist
          appliedIncentives.forEach(({ id }) => {
            if (id && !incentivesMapById[id]) {
              incentiveFeedbackMap[id] = [REWARD_NOT_AVAILABLE_EVALUATION_RESULT];
            }
          });
          evaluationResult = incentiveFeedbackMap;
        }
      } catch (e) {
        logger.error(`Rewards evaluation error: ${e}`);

        throw e;
      }

      return evaluationResult;
    },
    [client]
  );

  const evaluateLoyaltyIncentives = useCallback(
    async (params: EvaluateLoyaltyIncentivesParams) => {
      if (params.incentiveType === LoyaltyPromotionType.OFFER) {
        return await evaluateLoyaltyOffers(getLoyaltyEvaluationParams(params));
      }

      if (params.incentiveType === LoyaltyPromotionType.REWARD) {
        return await evaluateLoyaltyRewards(getLoyaltyEvaluationParams(params));
      }

      return null;
    },
    [evaluateLoyaltyOffers, evaluateLoyaltyRewards]
  );

  return { evaluateLoyaltyIncentives, evaluateLoyaltyOffers, evaluateLoyaltyRewards };
};
